import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateRoleBody,
  type RoleListParams,
  type UpdateRoleBody,
  adminClient,
} from '../lib/admin-client.js';

export const ROLE_KEYS = {
  all: (tenantId: string) => ['admin-roles', tenantId] as const,
  list: (tenantId: string, params: RoleListParams) =>
    ['admin-roles', tenantId, 'list', params] as const,
  detail: (tenantId: string, roleId: string) =>
    ['admin-roles', tenantId, 'detail', roleId] as const,
};

export function useAdminRoles(tenantId: string, params: RoleListParams = {}) {
  return useQuery({
    queryKey: ROLE_KEYS.list(tenantId, params),
    queryFn: () => adminClient.listRoles(tenantId, params),
    enabled: tenantId.length > 0,
    staleTime: 30_000,
  });
}

export function useAdminRole(tenantId: string, roleId: string) {
  return useQuery({
    queryKey: ROLE_KEYS.detail(tenantId, roleId),
    queryFn: () => adminClient.getRole(tenantId, roleId),
    enabled: tenantId.length > 0 && roleId.length > 0,
  });
}

export function useCreateRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleBody) => adminClient.createRole(tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLE_KEYS.all(tenantId) });
    },
  });
}

export function useUpdateRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, body }: { roleId: string; body: UpdateRoleBody }) =>
      adminClient.updateRole(tenantId, roleId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ROLE_KEYS.all(tenantId) });
      void qc.invalidateQueries({ queryKey: ROLE_KEYS.detail(tenantId, vars.roleId) });
    },
  });
}

export function useDeleteRole(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => adminClient.deleteRole(tenantId, roleId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLE_KEYS.all(tenantId) });
    },
  });
}
